/* ============================================================================
   Native (OS-level) notifications through the browser Notification API.

   Permission is asked for only from a click (see SidebarNotifications) — Safari
   ignores a request that is not inside a user gesture, and Chrome demotes it
   to a quiet icon in the address bar.

   Once the user has blocked the site, no page can prompt again: every browser
   resolves requestPermission() to 'denied' without showing anything, and only
   the user can reset it in the browser's own UI (unblockInstructions). The
   page can only notice when they have done so (watchNotificationPermission).
   ========================================================================== */
export const notificationsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const getNotificationPermission = () =>
  notificationsSupported() ? Notification.permission : 'unsupported'

export const requestNotificationPermission = () => {
  if (!notificationsSupported()) return Promise.resolve('unsupported')
  // Safari < 15 takes a callback and returns undefined
  return new Promise((resolve) => {
    const result = Notification.requestPermission(resolve)
    if (result && typeof result.then === 'function') result.then(resolve)
  })
}

/**
 * Calls `onChange(permission)` when the user changes the site's notification
 * setting in the browser, without a reload. Returns the unsubscribe function.
 * Browsers without the Permissions API for notifications just never call it;
 * the 10s poll in SidebarNotifications re-reads the permission anyway.
 */
export const watchNotificationPermission = (onChange) => {
  if (!notificationsSupported() || !navigator.permissions?.query) return () => {}
  let status = null
  let cancelled = false
  const handler = () => onChange(getNotificationPermission())
  navigator.permissions
    .query({ name: 'notifications' })
    .then((result) => {
      if (cancelled) return
      status = result
      status.addEventListener('change', handler)
    })
    .catch(() => {})
  return () => {
    cancelled = true
    if (status) status.removeEventListener('change', handler)
  }
}

/** Where this browser keeps the per-site switch the page cannot flip itself. */
export const unblockInstructions = () => {
  const ua = navigator.userAgent
  if (/firefox|fxios/i.test(ua)) {
    return 'Click the icon at the left of the address bar, clear the blocked "Send notifications" permission, then turn this on again.'
  }
  if (/^((?!chrome|chromium|crios|edg|android).)*safari/i.test(ua)) {
    return 'Open Safari → Settings → Websites → Notifications, set this site to Allow, then turn this on again.'
  }
  return 'Click the icon at the left of the address bar → Notifications → Allow. Alerts switch on by themselves once you do.'
}

/**
 * Shows one notification; a no-op without permission. `tag` collapses
 * duplicates when the same item is announced by two open tabs.
 */
export const showBrowserNotification = ({ title, body, tag, onClick }) => {
  if (getNotificationPermission() !== 'granted') return
  try {
    const notification = new Notification(title, { body, tag })
    notification.onclick = () => {
      window.focus()
      notification.close()
      if (onClick) onClick()
    }
  } catch (error) {
    // Chrome on Android only allows notifications through a service worker
    console.error('BROWSER_NOTIFICATION', error)
  }
}
